import { usePageSetupContext } from '@context/PageSetupContext';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import { DenominatorLabelValues } from '@services/api/deckManagement/graphql/schema/API';
import { PageGranularity } from 'types/Page.types';
import { DenominatorSelectLabels, REPORTING_PERIODS } from 'types/PageSetup.types';

const PageDetails = () => {
  const {
    pageName,
    setPageName,
    tocName,
    setTocName,
    pageHeaderName,
    setPageHeaderName,
    // reportingPeriods, TODO: Uncomment when User selection of Reporting Periods is allowed
    setReportingPeriods,
    denominatorLabel,
    setDenominatorLabel,
  } = usePageSetupContext();

  /**
   * TODO: Currently unused, as Reporting Periods are not customizable. Remove this comment when User selection is allowed.
   * Handle updating reporting periods
   * @param event - Select change event
   * @returns void
   */
  const handleReportingPeriodChange = (event: SelectChangeEvent<PageGranularity[]>) => {
    const {
      target: { value },
    } = event;
    // Parse stringified value to update periods
    setReportingPeriods(typeof value === 'string' ? (value.split(',') as PageGranularity[]) : value);
  };

  /**
   * Handle user changes to denominator label selection
   * @param event - Select change event
   * @returns void
   */
  const handleDenominatorLabelChange = (event: SelectChangeEvent<string>) => {
    const {
      target: { value },
    } = event;

    setDenominatorLabel(value === 'NO_DENOMINATOR_REQUIRED' ? null : (value as DenominatorLabelValues));
  };

  return (
    <Box className='page-details-step'>
      <TextField
        label='Page Name'
        value={pageName}
        onChange={(e) => setPageName(e.target.value)}
        fullWidth
        margin='normal'
        variant='outlined'
        size='small'
      />
      <TextField
        label='TOC Name'
        value={tocName}
        onChange={(e) => setTocName(e.target.value)}
        fullWidth
        margin='normal'
        variant='outlined'
        size='small'
      />
      <TextField
        label='Page Header Name'
        value={pageHeaderName}
        onChange={(e) => setPageHeaderName(e.target.value)}
        fullWidth
        margin='normal'
        variant='outlined'
        size='small'
      />
      <FormControl fullWidth margin='normal' variant='outlined' size='small'>
        <InputLabel>Reporting Periods</InputLabel>
        {/* TODO: For now, disable selection and set defaults. When user modification is allowed, set to value 'reportingPeriods' from context */}
        <Select
          multiple
          disabled
          value={[...REPORTING_PERIODS]}
          onChange={handleReportingPeriodChange}
          input={<OutlinedInput label='Reporting Periods' />}
          renderValue={(periods) => (
            <Box className='period-selected-chips'>
              {periods.map((period) => (
                <Chip className='period-chip' key={period} label={period} />
              ))}
            </Box>
          )}
        >
          {REPORTING_PERIODS.map((grain) => (
            <MenuItem key={grain} value={grain}>
              {grain}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth margin='normal' variant='outlined' size='small'>
        {/* TODO: Expand wizard on % Denominator action. After backend support, there should be a change to add a Step that users can set % Denominator drivers */}
        <InputLabel id='percent-denominator-label'>% Denominator</InputLabel>
        <Select
          labelId='percent-denominator-label'
          label='% Denominator'
          value={denominatorLabel ?? 'NO_DENOMINATOR_REQUIRED'}
          onChange={handleDenominatorLabelChange}
        >
          {Object.entries(DenominatorSelectLabels).map(([labelKey, labelValue], idx) => (
            <MenuItem key={`denominator-select-${idx}`} value={labelKey}>
              {labelValue}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default PageDetails;
